import { useMemo, useState } from "react";

type Option = {
  id: string;
  label: string;
};

type Props = {
  options: Option[];
  placeholder: string;
  noResultsCopy: string;
  onSelect: (id: string | undefined) => void;
};

// Diacritics-insensitive so e.g. "Zilinskas" finds "Žilinskas".
const normalise = (str: string): string =>
  str
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const SearchableSelect = ({ options, placeholder, noResultsCopy, onSelect }: Props) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = normalise(query);
    if (!q) return options;
    return options.filter((option) => normalise(option.label).includes(q));
  }, [options, query]);

  const handlePick = (option: Option) => {
    setQuery(option.label);
    setOpen(false);
    onSelect(option.id);
  };

  return (
    <div className="relative">
      <input
        className="input-bordered input w-full bg-gray-200"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onSelect(undefined);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered.length > 0) handlePick(filtered[0]);
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg bg-base-100 shadow-lg">
          {filtered.length === 0 && <li className="px-4 py-3 text-sm opacity-60">{noResultsCopy}</li>}
          {filtered.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="w-full px-4 py-3 text-left hover:bg-base-200"
                onMouseDown={(e) => {
                  // mousedown fires before the input's blur, so the pick lands
                  // before the dropdown closes; preventDefault keeps focus stable.
                  e.preventDefault();
                  handlePick(option);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
